/**
 * Display vocabulary for the Aaim-operator collateral pages: Property
 * Valuations, Title Searches and Lien Verifications.
 *
 * Each of those pages carried its own status-to-color switch and printed the
 * raw API code through `status.replace('_', ' ')`, so NEEDS_REVIEW rendered as
 * a red "NEEDS REVIEW" badge while its own detail callout was amber, and the
 * Lien Verifications tabs filtered on CLEAR / ACCEPTABLE / REVIEW_REQUIRED /
 * UNACCEPTABLE, values the TaxLienRisk enum does not carry. The pages now read
 * one set of label maps and compose the Badge primitive.
 *
 * Maps keyed by a generated enum fail tsc when the spec adds a member. A value
 * outside the map prints "Unrecognized" with the received code in the title
 * attribute (utils/status-labels.tsx enumLabel), never the raw code and never a
 * guessed label.
 *
 * Red is reserved for calls to action and alerts, so no map below sends an
 * informational state to Badge 'info' (which renders in the accent red).
 */

import { Badge, EmptyState } from '@aaimplatform/ui';
import type { TitleSearchStatus, ValuationStatus } from '../api/client';
import type { LienRiskLevel } from '../hooks/useLienVerifications';
import type { components as PropertyTitleAdminComponents } from '@aaimplatform/institutional-types/property-title-admin';
import type { TenantNameState } from '../hooks/useTenantNames';
import { ABSENT_LABEL, UNRECOGNIZED_LABEL, enumLabel } from '../utils/status-labels';

/** The Badge primitive variants a status badge on these pages renders in. */
type BadgeVariantState = 'success' | 'warning' | 'error' | 'neutral';

type TaxLienRisk = PropertyTitleAdminComponents['schemas']['TaxLienRisk'];

/**
 * What a lien figure prints when the record carries none. ATTOM reports no
 * recorded liens, and an absent figure is an absence, never zero liens: a zero
 * asserts a clean title (KG 2026-09-27 title-search-and-lien-verification).
 */
export const LIEN_DATA_ABSENT = 'Not available';

/** What an absent tax lien risk prints, in the table and in the dialog alike. */
export const TAX_LIEN_NOT_ASSESSED = 'Not assessed';

/** What the tenant column prints once when a row names neither tenant nor loan. */
export const NOT_LINKED_LABEL = 'Not linked';

/** What the tenant column prints when a tenant id does not resolve to a tenant. */
export const UNKNOWN_TENANT_LABEL = 'Unknown tenant';

// ---------------------------------------------------------------------------
// Label and tone maps
// ---------------------------------------------------------------------------

export const VALUATION_STATUS_LABEL: Record<ValuationStatus, string> = {
  CERTIFIED: 'Certified',
  PENDING: 'Pending',
  NEEDS_REVIEW: 'Needs review',
  FAILED: 'Failed',
};

const VALUATION_STATUS_TONE: Record<ValuationStatus, BadgeVariantState> = {
  CERTIFIED: 'success',
  PENDING: 'warning',
  // The detail dialog's review callout is the warning tint; the badge agrees.
  NEEDS_REVIEW: 'warning',
  FAILED: 'error',
};

export const TITLE_SEARCH_STATUS_LABEL: Record<TitleSearchStatus, string> = {
  CLEAR: 'Clear',
  COMPLETE: 'Complete',
  IN_PROGRESS: 'In progress',
  ISSUES_FOUND: 'Issues found',
  FAILED: 'Failed',
};

const TITLE_SEARCH_STATUS_TONE: Record<TitleSearchStatus, BadgeVariantState> = {
  CLEAR: 'success',
  COMPLETE: 'neutral',
  IN_PROGRESS: 'warning',
  ISSUES_FOUND: 'error',
  FAILED: 'error',
};

export const LIEN_RISK_LEVEL_LABEL: Record<LienRiskLevel, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

const LIEN_RISK_LEVEL_TONE: Record<LienRiskLevel, BadgeVariantState> = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'error',
  CRITICAL: 'error',
};

/**
 * LienVerification.taxLienRisk is the nullable TaxLienRisk enum in
 * property-title-admin.openapi.yaml (op-32). Null is an absent assessment and
 * prints {@link TAX_LIEN_NOT_ASSESSED}; the enum has no UNKNOWN member.
 */
export const TAX_LIEN_RISK_LABEL: Record<TaxLienRisk, string> = {
  NONE: 'Clear',
  LOW: 'Low',
  ELEVATED: 'Review required',
  CRITICAL: 'Critical',
};

const TAX_LIEN_RISK_TONE: Record<TaxLienRisk, BadgeVariantState> = {
  NONE: 'success',
  LOW: 'neutral',
  ELEVATED: 'warning',
  CRITICAL: 'error',
};

/** Filter options for a label map, in the map's declared order. */
export function labelOptions<K extends string>(
  map: Record<K, string>,
): Array<{ value: K; label: string }> {
  return (Object.keys(map) as K[]).map((value) => ({ value, label: map[value] }));
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

function toneFor<K extends string>(
  tones: Record<K, BadgeVariantState>,
  value: string,
): BadgeVariantState {
  return Object.prototype.hasOwnProperty.call(tones, value) ? tones[value as K] : 'neutral';
}

function CodeBadge<K extends string>({
  map,
  tones,
  value,
}: {
  map: Record<K, string>;
  tones: Record<K, BadgeVariantState>;
  value: string;
}) {
  const label = enumLabel(map, value);
  return (
    <span className="inline-flex" title={label === UNRECOGNIZED_LABEL ? value : undefined}>
      <Badge variant={toneFor(tones, value)} size="small">
        {label}
      </Badge>
    </span>
  );
}

export function ValuationStatusBadge({ status }: { status: string }) {
  return <CodeBadge map={VALUATION_STATUS_LABEL} tones={VALUATION_STATUS_TONE} value={status} />;
}

export function TitleSearchStatusBadge({ status }: { status: string }) {
  return <CodeBadge map={TITLE_SEARCH_STATUS_LABEL} tones={TITLE_SEARCH_STATUS_TONE} value={status} />;
}

/**
 * The lien risk level as a badge. The spec requires it, but a verification
 * made without lien data carries none (KG 2026-09-27 ruling: null, with no
 * UNKNOWN member), and that prints as not assessed.
 */
export function LienRiskBadge({ level }: { level: string | null | undefined }) {
  if (!level) {
    return <span className="text-sm font-sans text-tertiary">{TAX_LIEN_NOT_ASSESSED}</span>;
  }
  return <CodeBadge map={LIEN_RISK_LEVEL_LABEL} tones={LIEN_RISK_LEVEL_TONE} value={level} />;
}

/** The tax lien risk as a badge; absent prints "Not assessed", never a check mark. */
export function TaxLienRiskBadge({ risk }: { risk: string | null | undefined }) {
  if (!risk) {
    return <span className="text-sm font-sans text-tertiary">{TAX_LIEN_NOT_ASSESSED}</span>;
  }
  return <CodeBadge map={TAX_LIEN_RISK_LABEL} tones={TAX_LIEN_RISK_TONE} value={risk} />;
}

// ---------------------------------------------------------------------------
// Lien figures
// ---------------------------------------------------------------------------

/** A number the record delivered, as opposed to one it omitted. */
export function isMeasured(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** "1 lien", "3 liens", or {@link LIEN_DATA_ABSENT} for an omitted count. */
export function formatLienCount(value: number | null | undefined): string {
  if (!isMeasured(value)) return LIEN_DATA_ABSENT;
  return `${value} ${value === 1 ? 'lien' : 'liens'}`;
}

// ---------------------------------------------------------------------------
// Tenant and loan column
// ---------------------------------------------------------------------------

/** The tenant display name for a row, or the reason it has none. */
export function tenantDisplayName(
  tenantId: string | null | undefined,
  names: ReadonlyMap<string, TenantNameState>,
): string {
  if (!tenantId) return ABSENT_LABEL;
  const entry = names.get(tenantId);
  if (!entry || entry.state === 'loading') return ABSENT_LABEL;
  return entry.state === 'resolved' ? entry.name : UNKNOWN_TENANT_LABEL;
}

/**
 * The tenant a row belongs to, by name, with its loan id beneath. A row with
 * neither prints {@link NOT_LINKED_LABEL} once, where the column printed two
 * stacked dashes.
 */
export function TenantLoanCell({
  tenantId,
  loanId,
  names,
}: {
  tenantId: string | null | undefined;
  loanId: string | null | undefined;
  names: ReadonlyMap<string, TenantNameState>;
}) {
  if (!tenantId && !loanId) {
    return <span className="text-sm font-sans text-tertiary">{NOT_LINKED_LABEL}</span>;
  }
  const entry = tenantId ? names.get(tenantId) : undefined;
  return (
    <div>
      <div
        className="text-sm font-sans text-primary leading-normal"
        title={entry?.state === 'unresolved' && tenantId ? tenantId : undefined}
      >
        {tenantId ? tenantDisplayName(tenantId, names) : 'No tenant'}
      </div>
      {loanId && (
        <div className="text-xs font-mono text-tertiary leading-normal">Loan {loanId}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty lists
// ---------------------------------------------------------------------------

/** The words for one collateral list's three empty states. */
export interface CollateralEmptyCopyConfig {
  /** Title when the list read failed. The page's alert carries the error. */
  failedTitle: string;
  /** Title when rows were read and the filters or search excluded all of them. */
  filteredTitle: string;
  /** Title when the read returned no rows. */
  emptyTitle: string;
  /** Why the read returned no rows. */
  emptyReason: string;
}

export interface CollateralListEmptyStateProps {
  copy: CollateralEmptyCopyConfig;
  readFailed: boolean;
  /** Rows were read, and the filters or search excluded every one. */
  filteredOut: boolean;
}

/**
 * The empty state for a collateral list, passed through DataGrid's emptyState.
 * The three pages printed one generic line under the column headers ("No title
 * searches found"), which named neither the reason nor the difference between
 * an empty read and a filter that excluded every row (op-g2-8, op-cons-7).
 */
export function CollateralListEmptyState({ copy, readFailed, filteredOut }: CollateralListEmptyStateProps) {
  if (readFailed) {
    return <EmptyState title={copy.failedTitle} description="The alert above names the error. Retry once it clears." />;
  }
  if (filteredOut) {
    return (
      <EmptyState
        title={copy.filteredTitle}
        description="Clear the filters or the search to see every row read."
      />
    );
  }
  return <EmptyState title={copy.emptyTitle} description={copy.emptyReason} />;
}

/**
 * Why Title Searches and Lien Verifications hold no rows: a request is refused
 * with 422 LIEN_DATA_UNAVAILABLE before anything is written, because the
 * property-data provider carries no recorded liens (KG 2026-09-27
 * title-search-and-lien-verification).
 */
export const LIEN_DATA_REFUSAL_REASON =
  'The property-data provider supplies no recorded liens today, so a new request is refused with Lien data unavailable and nothing is recorded.';
